/**
 * Template Builder Component
 *
 * Full template creation/editing UI for custom specification templates.
 * Allows users to define template sections that generate TipTap JSON structure.
 *
 * Part of PowerSpec V3 - Custom Template Builder
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Button,
  TextInput,
  Textarea,
  Select,
  ToggleSwitch,
  Alert,
  Badge
} from 'flowbite-react';
import {
  HiPlus,
  HiTrash,
  HiArrowUp,
  HiArrowDown,
  HiSave,
  HiX,
  HiEye,
  HiPencil
} from 'react-icons/hi';
import type { SpecificationTemplate } from '../api/specifications';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TemplateSection {
  id: string;
  heading: string;
  placeholder: string;
}

export interface TemplateBuilderProps {
  /** Existing template for editing; omit for create mode */
  template?: SpecificationTemplate;
  /** Called when the user saves the template */
  onSave: (data: {
    name: string;
    description: string;
    category: string;
    is_public: boolean;
    structure: Record<string, unknown>;
  }) => void;
  /** Called when the user cancels */
  onCancel: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORIES = [
  { value: 'root_cause', label: 'Root Cause Analysis' },
  { value: 'failure_mode', label: 'Failure Mode Analysis' },
  { value: 'agile', label: 'Agile' },
  { value: 'process_improvement', label: 'Process Improvement' },
  { value: 'technical', label: 'Technical' },
  { value: 'product', label: 'Product' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'custom', label: 'Custom' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let sectionCounter = 0;
function generateSectionId(): string {
  sectionCounter += 1;
  return `section-${Date.now()}-${sectionCounter}`;
}

/**
 * Parse existing TipTap structure into editable sections.
 * Expected format: { type: "doc", content: [ heading, paragraph, heading, paragraph, ... ] }
 */
function parseSectionsFromStructure(structure: Record<string, unknown> | undefined): TemplateSection[] {
  if (!structure) return [];

  const content = (structure as { content?: unknown[] }).content;
  if (!Array.isArray(content)) return [];

  const sections: TemplateSection[] = [];
  let currentHeading = '';

  for (const node of content) {
    const n = node as { type?: string; content?: Array<{ text?: string }> };
    if (n.type === 'heading') {
      currentHeading = n.content?.[0]?.text || '';
    } else if (n.type === 'paragraph') {
      const text = n.content?.[0]?.text || '';
      sections.push({
        id: generateSectionId(),
        heading: currentHeading,
        placeholder: text,
      });
      currentHeading = '';
    }
  }

  // If there's a trailing heading without a paragraph, add it
  if (currentHeading) {
    sections.push({
      id: generateSectionId(),
      heading: currentHeading,
      placeholder: '',
    });
  }

  return sections;
}

/**
 * Convert sections to TipTap JSON document structure.
 */
function sectionsToTipTap(sections: TemplateSection[]): Record<string, unknown> {
  const content: unknown[] = [];

  for (const section of sections) {
    content.push({
      type: 'heading',
      attrs: { level: 2 },
      content: [{ type: 'text', text: section.heading || 'Untitled Section' }],
    });
    content.push({
      type: 'paragraph',
      content: [{ type: 'text', text: section.placeholder || 'Enter content here...' }],
    });
  }

  return { type: 'doc', content };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({ template, onSave, onCancel }) => {
  const isEditMode = !!template;

  // Form state
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [category, setCategory] = useState(template?.category || 'custom');
  const [isPublic, setIsPublic] = useState(template?.is_public || false);
  const [sections, setSections] = useState<TemplateSection[]>(() =>
    isEditMode
      ? parseSectionsFromStructure(template?.structure as Record<string, unknown> | undefined)
      : [{ id: generateSectionId(), heading: 'Overview', placeholder: 'Describe the purpose and scope...' }]
  );

  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Computed TipTap structure
  const tipTapStructure = useMemo(() => sectionsToTipTap(sections), [sections]);

  // ---------------------------------------------------------------------------
  // Section management
  // ---------------------------------------------------------------------------

  const addSection = useCallback(() => {
    setSections(prev => [
      ...prev,
      { id: generateSectionId(), heading: '', placeholder: '' },
    ]);
  }, []);

  const removeSection = useCallback((id: string) => {
    setSections(prev => prev.filter(s => s.id !== id));
  }, []);

  const updateSection = useCallback((id: string, field: 'heading' | 'placeholder', value: string) => {
    setSections(prev =>
      prev.map(s => (s.id === id ? { ...s, [field]: value } : s))
    );
  }, []);

  const moveSection = useCallback((id: string, direction: 'up' | 'down') => {
    setSections(prev => {
      const idx = prev.findIndex(s => s.id === id);
      if (idx < 0) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;

      const next = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  const handleSave = useCallback(() => {
    // Validate
    if (!name.trim()) {
      setValidationError('Template name is required');
      return;
    }
    if (sections.length === 0) {
      setValidationError('At least one section is required');
      return;
    }
    const emptyHeading = sections.find(s => !s.heading.trim());
    if (emptyHeading) {
      setValidationError('All sections must have a heading');
      return;
    }

    setValidationError(null);

    onSave({
      name: name.trim(),
      description: description.trim(),
      category,
      is_public: isPublic,
      structure: tipTapStructure,
    });
  }, [name, description, category, isPublic, sections, tipTapStructure, onSave]);

  // Clear validation error when user edits
  useEffect(() => {
    if (validationError) setValidationError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, sections]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">
          {isEditMode ? 'Edit Template' : 'Create Template'}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            color="gray"
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? (
              <><HiPencil className="w-4 h-4 mr-1" /> Editor</>
            ) : (
              <><HiEye className="w-4 h-4 mr-1" /> Preview</>
            )}
          </Button>
        </div>
      </div>

      {/* Validation error */}
      {validationError && (
        <Alert color="failure" onDismiss={() => setValidationError(null)}>
          {validationError}
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left column: Form fields and section builder */}
        <div className="space-y-4">
          {/* Template metadata */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Template Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <TextInput
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g., Sprint Retrospective, API Design Review"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <Textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Briefly describe when to use this template..."
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <Select value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="flex items-center gap-3">
                <ToggleSwitch
                  checked={isPublic}
                  onChange={setIsPublic}
                  label="Make publicly visible"
                />
              </div>
            </div>
          </Card>

          {/* Section Builder */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Sections ({sections.length})
              </h3>
              <Button color="blue" size="sm" onClick={addSection}>
                <HiPlus className="w-4 h-4 mr-1" />
                Add Section
              </Button>
            </div>

            {sections.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No sections yet. Click "Add Section" to start building your template.
              </p>
            ) : (
              <div className="space-y-3">
                {sections.map((section, index) => (
                  <Card key={section.id} className="bg-gray-50">
                    <div className="space-y-3">
                      {/* Section controls */}
                      <div className="flex items-center justify-between">
                        <Badge color="gray" size="sm">
                          Section {index + 1}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            color="gray"
                            size="xs"
                            disabled={index === 0}
                            onClick={() => moveSection(section.id, 'up')}
                            title="Move up"
                          >
                            <HiArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            color="gray"
                            size="xs"
                            disabled={index === sections.length - 1}
                            onClick={() => moveSection(section.id, 'down')}
                            title="Move down"
                          >
                            <HiArrowDown className="w-3 h-3" />
                          </Button>
                          <Button
                            color="failure"
                            size="xs"
                            onClick={() => removeSection(section.id)}
                            title="Remove section"
                          >
                            <HiTrash className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>

                      {/* Section fields */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Heading
                        </label>
                        <TextInput
                          sizing="sm"
                          value={section.heading}
                          onChange={e => updateSection(section.id, 'heading', e.target.value)}
                          placeholder="Section heading..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Placeholder content
                        </label>
                        <Textarea
                          value={section.placeholder}
                          onChange={e => updateSection(section.id, 'placeholder', e.target.value)}
                          placeholder="Placeholder text for this section..."
                          rows={2}
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Right column: Preview panel */}
        <div>
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {showPreview ? 'Template Preview' : 'Structure Preview'}
            </h3>

            {showPreview ? (
              /* Rich preview */
              <div className="prose max-w-none">
                {sections.length === 0 ? (
                  <p className="text-gray-400 italic">No sections to preview</p>
                ) : (
                  sections.map(section => (
                    <div key={section.id} className="mb-4">
                      <h2 className="text-lg font-bold text-gray-900 border-b pb-1 mb-2">
                        {section.heading || 'Untitled Section'}
                      </h2>
                      <p className="text-gray-500 italic">
                        {section.placeholder || 'Enter content here...'}
                      </p>
                    </div>
                  ))
                )}
              </div>
            ) : (
              /* JSON structure preview */
              <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-auto max-h-96 text-gray-700">
                {JSON.stringify(tipTapStructure, null, 2)}
              </pre>
            )}
          </Card>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button color="gray" onClick={onCancel}>
          <HiX className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button color="blue" onClick={handleSave}>
          <HiSave className="w-4 h-4 mr-2" />
          {isEditMode ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
};

export default TemplateBuilder;
